import { InteractionRequiredAuthError, IPublicClientApplication } from "@azure/msal-browser";
import { handleClientSideErrors } from "./utils/error-handler";
import { loginRequest } from "./utils/auth-config";
import { callGetUserDataApi } from "./api";
import { getI18n } from "@/lib/i18n";

const MAX_RETRY_ATTEMPTS = 3;
let retryGetMiddletierToken = 0;

interface TokenWithValidation {
  accessToken: string;
  user: {
    id: string;
    displayName: string;
    email: string;
  };
}

/**
 * Main function to get user data. It tries SSO first and falls back to a dialog.
 */
export async function getTokenWithValidation(msalInstance: IPublicClientApplication): Promise<TokenWithValidation> {
  try {
    const middletierToken = await getMiddletierTokenSSO();
    let response = await callGetUserDataApi(middletierToken);

    // Handle MFA claim challenge
    if (response.claims) {
      const mfaMiddletierToken = await getMiddletierTokenSSO(response.claims);
      response = await callGetUserDataApi(mfaMiddletierToken);
    }

    // Handle AAD errors that come with a 200 OK response
    if (response.error) {
      return handleAADErrors(response, msalInstance);
    }

    retryGetMiddletierToken = 0; // Reset on success
    const result: TokenWithValidation = {
      accessToken: middletierToken,
      user: {
        id: response.id,
        displayName: response.displayName,
        email: response.mail || response.userPrincipalName,
      },
    };
    return result;
  } catch (exception: any) {
    console.error("SSO authentication exception:", exception);
    // If SSO fails, decide whether to show the fallback dialog
    if (handleClientSideErrors(exception)) {
      console.warn("SSO failed. Falling back to dialog authentication.");
      return dialogFallback(msalInstance);
    } else {
      // Re-throw if the error handler decides not to use fallback
      throw exception;
    }
  }
}

/**
 * Tries to get a token silently via Office SSO.
 */
async function getMiddletierTokenSSO(authChallenge?: string): Promise<string> {
  const options: Office.AuthOptions = {
    allowSignInPrompt: true,
    allowConsentPrompt: true,
    forMSGraphAccess: true,
  };

  if (authChallenge) {
    options.authChallenge = authChallenge;
  }
  try {
    const token = await OfficeRuntime.auth.getAccessToken(options);
    if (!token) {
      throw new Error(getI18n().t("connection-error-default"));
    }
    return token;
  } catch (error) {
    console.error("getAccessToken error:", error);
    throw error; // Re-throw to be caught by the main getUserData function
  }
}

/**
 * Handles specific AAD errors, like an expired token.
 */
async function handleAADErrors(response: any, msalInstance: IPublicClientApplication): Promise<TokenWithValidation> {
  // Check for expired token error AADSTS500133
  if (response.error_description?.includes("AADSTS500133") && retryGetMiddletierToken < MAX_RETRY_ATTEMPTS) {
    retryGetMiddletierToken++;
    console.warn(
      `Retrying getUserData due to expired token (attempt ${retryGetMiddletierToken}/${MAX_RETRY_ATTEMPTS})`,
    );

    // Exponential backoff could be added here
    await new Promise((resolve) => setTimeout(resolve, 500 * retryGetMiddletierToken));
    return getTokenWithValidation(msalInstance);
  } else {
    // For other AAD errors or max retries, fall back to the dialog
    console.error("Unhandled AAD error or max retries reached. Falling back to dialog.");
    return dialogFallback(msalInstance);
  }
}

/**
 * Fallback authentication using the Office Dialog API.
 */
async function dialogFallback(msalInstance: IPublicClientApplication): Promise<TokenWithValidation> {
  try {
    // First, try to get a token silently with MSAL. This will succeed if the user
    // has a valid session from a previous login.
    const account = msalInstance.getActiveAccount();
    if (account) {
      const silentRequest = { ...loginRequest, account };
      const silentResponse = await msalInstance.acquireTokenSilent(silentRequest);
      const response = await callGetUserDataApi(silentResponse.accessToken);
      if (response && !response.error) {
        return {
          accessToken: silentResponse.accessToken,
          user: {
            id: response.id,
            displayName: response.displayName,
            email: response.mail || response.userPrincipalName,
          },
        };
      } else if (response.error) {
        console.warn("AAD error during silent acquisition:", response.error);
      }
    }
  } catch (error) {
    // If silent acquisition fails, proceed with the interactive dialog
    if (!(error instanceof InteractionRequiredAuthError)) {
      console.error("MSAL silent token acquisition failed:", error);
    }
  }

  // If silent acquisition is not possible, show the login popup
  const accessToken = await showLoginPopup();
  if (accessToken) {
    const response = await callGetUserDataApi(accessToken);
    if (response && !response.error) {
      return {
        accessToken: accessToken,
        user: {
          id: response.id,
          displayName: response.displayName,
          email: response.mail || response.userPrincipalName,
        },
      };
    } else if (response.error) {
      console.error("AAD error after dialog authentication:", response.error);
      throw new Error(getI18n().t("connection-error-default"));
    }
  } else {
    throw new Error(getI18n().t("connection-error-default"));
  }
}

/**
 * Uses the Office Dialog API to show a sign-in popup.
 */
function showLoginPopup(): Promise<string | null> {
  const url = "/fallback-auth-dialog.html";
  const fullUrl = `${location.protocol}//${location.hostname}${location.port ? ":" + location.port : ""}${url}`;

  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(fullUrl, { height: 60, width: 30 }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(result.error.message));
        return;
      }

      const loginDialog = result.value;
      loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
        loginDialog.close();
        const message = JSON.parse(arg.message);
        if (message.status === "success") {
          resolve(message.accessToken);
        } else {
          console.error("Dialog authentication failed:", message.result);
          reject(new Error(getI18n().t("connection-error-default")));
        }
      });
    });
  });
}
